import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Card, 
  CardContent, 
  Divider, 
  TextField, 
  Button, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemIcon,
  Accordion, 
  AccordionSummary, 
  AccordionDetails,
  InputAdornment,
  Chip,
  Avatar,
  IconButton,
  Tabs,
  Tab,
  Link,
  Alert,
  Snackbar
} from '@mui/material';
import { 
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  QuestionAnswer as QuestionAnswerIcon,
  LiveHelp as LiveHelpIcon,
  School as SchoolIcon,
  VideoLibrary as VideoLibraryIcon,
  MenuBook as MenuBookIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Chat as ChatIcon,
  Headset as HeadsetIcon,
  Forum as ForumIcon,
  Assignment as AssignmentIcon,
  Lightbulb as LightbulbIcon,
  Send as SendIcon,
  Download as DownloadIcon,
  PlayArrow as PlayArrowIcon,
  Bookmark as BookmarkIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon
} from '@mui/icons-material';
import MainLayout from '../layout/MainLayout';

// Beispieldaten für FAQs
const faqData = [
  {
    category: 'Allgemein',
    questions: [
      {
        id: 'faq-1',
        question: 'Was ist die ARGUS-Platform?',
        answer: 'Die ARGUS-Platform ist eine sichere Lösung für Krankenhäuser und andere datenhaltende Institutionen, um Datenanfragen von Pharma- und Industrieunternehmen zu verwalten. Sie ermöglicht es Ihnen, eingehende Anfragen zu prüfen, deren Machbarkeit zu bewerten, Preise zu verhandeln und nach erfolgreicher Bezahlung Analyseergebnisse zu übermitteln.'
      },
      {
        id: 'faq-2',
        question: 'Wie kann ich mich bei der ARGUS-Platform registrieren?',
        answer: 'Die Registrierung bei der ARGUS-Platform erfolgt über Ihre Institution. Bitte wenden Sie sich an Ihren IT-Administrator oder kontaktieren Sie unser Support-Team unter support@argus.tech, um Informationen zur Einrichtung und Registrierung zu erhalten.'
      },
      {
        id: 'faq-3',
        question: 'Ist die ARGUS-Platform DSGVO-konform?',
        answer: 'Ja, die ARGUS-Platform wurde unter strikter Einhaltung der Datenschutz-Grundverordnung (DSGVO) entwickelt. Alle Daten werden verschlüsselt gespeichert und übertragen, und wir implementieren umfassende Maßnahmen zum Schutz personenbezogener Daten. Die Daten verlassen zu keinem Zeitpunkt Ihre Institution.'
      }
    ]
  },
  {
    category: 'Anfragen verwalten',
    questions: [
      {
        id: 'faq-4',
        question: 'Wie prüfe ich eine eingehende Datenanfrage?',
        answer: 'Eingehende Anfragen erscheinen im Dashboard unter "Neue Anfragen". Klicken Sie auf eine Anfrage, um Details wie Kohortendefinition, gewünschte Variablen und Analyseziele einzusehen. Unter "Machbarkeitsprüfung" können Sie bewerten, ob die Anfrage mit Ihren vorhandenen Daten durchführbar ist.'
      },
      {
        id: 'faq-5',
        question: 'Wie verhandle ich den Preis für eine Datenanfrage?',
        answer: 'Nach der Machbarkeitsprüfung können Sie im Bereich "Verhandlung" einen Preisvorschlag machen. Berücksichtigen Sie dabei Faktoren wie Komplexität der Analyse, Umfang der Daten und erforderlichen Arbeitsaufwand. Sie können mit dem Anfragenden über die integrierte Chat-Funktion kommunizieren, um Details zu klären.'
      },
      {
        id: 'faq-6',
        question: 'Wie lehne ich eine Anfrage ab?',
        answer: 'Wenn eine Anfrage nicht durchführbar ist oder nicht den Richtlinien Ihrer Institution entspricht, können Sie sie ablehnen. Navigieren Sie zur Detailansicht der Anfrage und wählen Sie "Ablehnen". Geben Sie eine Begründung an, damit der Anfragende die Gründe versteht und ggf. eine überarbeitete Anfrage stellen kann.'
      }
    ]
  },
  {
    category: 'Datenanalyse',
    questions: [
      {
        id: 'faq-7',
        question: 'Wie führe ich eine Datenanalyse durch?',
        answer: 'Nach Zahlungseingang können Sie die Analyse starten. Navigieren Sie zur Anfrage und wählen Sie "Analyse starten". Je nach Anforderung können Sie zwischen automatisierten Analysetools der ARGUS-Suite wählen oder die Analyse manuell durchführen. Der Fortschritt wird in der Ausführungsübersicht angezeigt.'
      },
      {
        id: 'faq-8',
        question: 'Kann ich externe Analyseskripte verwenden?',
        answer: 'Ja, Sie können eigene oder vom Anfragenden bereitgestellte Skripte in R oder Python verwenden. Diese werden in einer sicheren Sandbox-Umgebung ausgeführt, um Ihre Dateninfrastruktur zu schützen. Alle Skripte werden vor der Ausführung auf Sicherheitsrisiken geprüft.'
      },
      {
        id: 'faq-9',
        question: 'Wie übermittle ich die Analyseergebnisse?',
        answer: 'Nach Abschluss der Analyse können Sie die Ergebnisse über die Plattform übermitteln. Navigieren Sie zur Anfrage und wählen Sie "Ergebnisse übermitteln". Sie können Berichte, Tabellen und Grafiken hochladen, die dann dem Anfragenden zur Verfügung gestellt werden. Alle Ergebnisse werden automatisch anonymisiert.'
      }
    ]
  },
  {
    category: 'Abrechnung',
    questions: [
      {
        id: 'faq-10',
        question: 'Wie verfolge ich Zahlungseingänge?',
        answer: 'Im Bereich "Finanzen" können Sie den Status aller Zahlungen einsehen. Nach Abschluss der Preisverhandlung wird eine Rechnung erstellt und dem Anfragenden zugesandt. Sobald die Zahlung eingeht, wird der Status automatisch aktualisiert und Sie können mit der Analyse beginnen.'
      },
      {
        id: 'faq-11',
        question: 'Welche Zahlungsmethoden werden unterstützt?',
        answer: 'Die ARGUS-Platform unterstützt verschiedene Zahlungsmethoden, darunter Banküberweisung und Rechnungsstellung. Die Zahlungsabwicklung erfolgt sicher und transparent. Sie können Ihre bevorzugten Zahlungsmethoden in den Einstellungen konfigurieren.'
      },
      {
        id: 'faq-12',
        question: 'Wie werden die Einnahmen verteilt?',
        answer: 'Die Einnahmen aus Datenanfragen gehen direkt an Ihre Institution, abzüglich einer kleinen Servicegebühr für die ARGUS-Platform. Die genaue Gebührenstruktur finden Sie in Ihrem Vertrag. In den Finanzberichten können Sie detaillierte Aufstellungen aller Transaktionen einsehen.'
      }
    ]
  }
];

// Beispieldaten für Tutorials
const tutorialData = [
  {
    id: 'tut-1',
    title: 'Erste Schritte mit der ARGUS-Platform',
    description: 'Lernen Sie die Grundlagen der ARGUS-Platform kennen und erfahren Sie, wie Sie das System für Ihre Institution einrichten.',
    type: 'video',
    duration: '6:45',
    url: '#',
    thumbnail: 'https://via.placeholder.com/300x180'
  },
  {
    id: 'tut-2',
    title: 'Anfragen prüfen und bewerten',
    description: 'Erfahren Sie, wie Sie eingehende Datenanfragen effizient prüfen und deren Machbarkeit anhand Ihrer vorhandenen Daten bewerten können.',
    type: 'article',
    readTime: '10 min',
    url: '#',
    thumbnail: 'https://via.placeholder.com/300x180'
  },
  {
    id: 'tut-3',
    title: 'Preisverhandlung und Kommunikation',
    description: 'Lernen Sie, wie Sie Preise für Datenanfragen festlegen und effektiv mit anfragenden Institutionen kommunizieren können.',
    type: 'webinar',
    duration: '40:00',
    url: '#',
    thumbnail: 'https://via.placeholder.com/300x180'
  },
  {
    id: 'tut-4',
    title: 'Datenanalyse mit der ARGUS-Tool-Suite',
    description: 'Entdecken Sie die automatisierten Analysetools der ARGUS-Suite und wie Sie diese für verschiedene Anforderungen einsetzen können.',
    type: 'video',
    duration: '15:20',
    url: '#',
    thumbnail: 'https://via.placeholder.com/300x180'
  },
  {
    id: 'tut-5',
    title: 'Datenschutz und Compliance',
    description: 'Verstehen Sie die Datenschutz- und Compliance-Funktionen der ARGUS-Platform und wie Sie die Sicherheit sensibler Patientendaten gewährleisten.',
    type: 'article',
    readTime: '14 min',
    url: '#',
    thumbnail: 'https://via.placeholder.com/300x180'
  },
  {
    id: 'tut-6',
    title: 'Finanzberichte und Abrechnungen',
    description: 'Erfahren Sie, wie Sie Zahlungseingänge verfolgen, Finanzberichte erstellen und die Abrechnung von Datenanfragen verwalten können.',
    type: 'webinar',
    duration: '35:00',
    url: '#',
    thumbnail: 'https://via.placeholder.com/300x180'
  }
];

// Beispieldaten für Support-Kontakte
const supportContacts = [
  {
    title: 'E-Mail Support',
    description: 'Senden Sie uns eine E-Mail und erhalten Sie innerhalb von 24 Stunden eine Antwort.',
    icon: <EmailIcon fontSize="large" />,
    contact: 'support@argus.tech',
    action: 'E-Mail senden',
    url: 'mailto:support@argus.tech'
  },
  {
    title: 'Telefon-Hotline',
    description: 'Sprechen Sie direkt mit unserem Support-Team während der Geschäftszeiten (Mo-Fr, 9-17 Uhr).',
    icon: <PhoneIcon fontSize="large" />,
    contact: '+49 30 123456789',
    action: 'Anrufen',
    url: 'tel:+4930123456789'
  },
  {
    title: 'Live-Chat',
    description: 'Chatten Sie in Echtzeit mit unserem Support-Team für schnelle Hilfe bei dringenden Fragen.',
    icon: <ChatIcon fontSize="large" />,
    contact: 'Verfügbar während der Geschäftszeiten',
    action: 'Chat starten',
    url: '#'
  },
  {
    title: 'Institutioneller Support',
    description: 'Spezieller Support für Krankenhäuser und Forschungseinrichtungen mit dedizierten Ansprechpartnern.',
    icon: <HeadsetIcon fontSize="large" />,
    contact: 'institutions@argus.tech',
    action: 'Kontakt aufnehmen',
    url: 'mailto:institutions@argus.tech'
  }
];

// Beispieldaten für Dokumentation
const documentationData = [
  {
    title: 'Administratorhandbuch',
    description: 'Umfassende Anleitung zur Einrichtung und Verwaltung der ARGUS-Platform in Ihrer Institution.',
    icon: <MenuBookIcon />,
    url: '#',
    format: 'PDF',
    size: '5.1 MB'
  },
  {
    title: 'Benutzerhandbuch',
    description: 'Detaillierte Anleitung zur täglichen Nutzung der ARGUS-Platform für Mitarbeiter in datenhaltenden Institutionen.',
    icon: <MenuBookIcon />,
    url: '#',
    format: 'PDF',
    size: '4.8 MB'
  },
  {
    title: 'API-Dokumentation',
    description: 'Technische Dokumentation für IT-Teams zur Integration der ARGUS-Platform mit bestehenden Systemen.',
    icon: <AssignmentIcon />,
    url: '#',
    format: 'HTML',
    size: 'Online'
  },
  {
    title: 'Datenschutz-Leitfaden',
    description: 'Detaillierte Informationen zu Datenschutzmaßnahmen, DSGVO-Compliance und sicherer Datenverarbeitung.',
    icon: <AssignmentIcon />,
    url: '#',
    format: 'PDF',
    size: '2.3 MB'
  },
  {
    title: 'Analysetools-Dokumentation',
    description: 'Technische Dokumentation zu den automatisierten Analysetools der ARGUS-Suite.',
    icon: <LightbulbIcon />,
    url: '#',
    format: 'PDF',
    size: '3.7 MB'
  }
];

const HelpSupportPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [expandedFaq, setExpandedFaq] = useState(false);
  const [ticketSubject, setTicketSubject] = useState('');
  const [ticketMessage, setTicketMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  const handleFaqChange = (panel) => (event, isExpanded) => {
    setExpandedFaq(isExpanded ? panel : false);
  };
  
  const handleTicketSubmit = (e) => {
    e.preventDefault();
    // Hier würde in einer realen Anwendung das Ticket gesendet werden
    console.log('Ticket submitted:', { subject: ticketSubject, message: ticketMessage });
    setTicketSubject('');
    setTicketMessage('');
    setSnackbarOpen(true);
  };
  
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };
  
  // Filtern der FAQs basierend auf dem Suchbegriff
  const filteredFaqs = faqData.map(category => ({
    ...category,
    questions: category.questions.filter(q => 
      q.question.toLowerCase().includes(searchTerm.toLowerCase()) || 
      q.answer.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(category => category.questions.length > 0);
  
  return (
    <MainLayout>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700, color: 'text.primary' }}>
          Hilfe & Support
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Finden Sie Antworten auf Ihre Fragen und erhalten Sie Unterstützung bei der Nutzung der ARGUS-Platform für Ihre Institution.
        </Typography>
        
        <Paper sx={{ mb: 3 }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange} 
            aria-label="help tabs"
            variant="scrollable"
            scrollButtons="auto"
            sx={{ 
              borderBottom: 1, 
              borderColor: 'divider',
              '& .MuiTab-root': {
                minHeight: 64,
                py: 2
              }
            }}
          >
            <Tab 
              icon={<QuestionAnswerIcon />} 
              label="FAQ" 
              iconPosition="start"
              sx={{ textTransform: 'none' }}
            />
            <Tab 
              icon={<SchoolIcon />} 
              label="Tutorials" 
              iconPosition="start"
              sx={{ textTransform: 'none' }}
            />
            <Tab 
              icon={<HeadsetIcon />} 
              label="Support kontaktieren" 
              iconPosition="start"
              sx={{ textTransform: 'none' }}
            />
            <Tab 
              icon={<MenuBookIcon />} 
              label="Dokumentation" 
              iconPosition="start"
              sx={{ textTransform: 'none' }}
            />
          </Tabs>
          
          <Box sx={{ p: 3 }}>
            {/* FAQ-Bereich */}
            {activeTab === 0 && (
              <Box>
                <TextField
                  fullWidth
                  placeholder="Suchen Sie nach Fragen oder Stichwörtern..."
                  variant="outlined"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{ mb: 3 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
                
                {filteredFaqs.length > 0 ? (
                  filteredFaqs.map((category, index) => (
                    <Box key={index} sx={{ mb: 4 }}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                        {category.category}
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      
                      {category.questions.map((faq) => (
                        <Accordion 
                          key={faq.id} 
                          expanded={expandedFaq === faq.id} 
                          onChange={handleFaqChange(faq.id)}
                          sx={{ 
                            mb: 1,
                            boxShadow: 'none',
                            border: '1px solid',
                            borderColor: 'divider',
                            '&:before': {
                              display: 'none',
                            },
                            '&.Mui-expanded': {
                              borderColor: 'primary.light',
                              bgcolor: 'rgba(255, 122, 0, 0.03)',
                            }
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls={`${faq.id}-content`}
                            id={`${faq.id}-header`}
                            sx={{ 
                              '& .MuiAccordionSummary-content': {
                                display: 'flex',
                                alignItems: 'center',
                              }
                            }}
                          >
                            <LiveHelpIcon sx={{ mr: 2, color: 'primary.main' }} />
                            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                              {faq.question}
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails sx={{ pl: 6 }}>
                            <Typography variant="body1" paragraph>
                              {faq.answer}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                              <Button 
                                size="small" 
                                startIcon={<ThumbUpIcon />}
                                sx={{ color: 'success.main' }}
                              >
                                Hilfreich
                              </Button>
                              <Button 
                                size="small" 
                                startIcon={<ThumbDownIcon />}
                                sx={{ color: 'text.secondary' }}
                              >
                                Nicht hilfreich
                              </Button>
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      ))}
                    </Box>
                  ))
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      Keine Ergebnisse gefunden
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Versuchen Sie es mit anderen Suchbegriffen oder kontaktieren Sie unseren Support.
                    </Typography>
                    <Button 
                      variant="contained" 
                      color="primary" 
                      sx={{ mt: 2 }}
                      onClick={() => setActiveTab(2)}
                    >
                      Support kontaktieren
                    </Button>
                  </Box>
                )}
              </Box>
            )}
            
            {/* Tutorials-Bereich */}
            {activeTab === 1 && (
              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Lernmaterialien und Tutorials
                </Typography>
                <Typography variant="body1" paragraph color="text.secondary">
                  Entdecken Sie unsere Sammlung von Tutorials, Videos und Artikeln, um das Beste aus der ARGUS-Platform für Ihre datenhaltende Institution herauszuholen.
                </Typography>
                
                <Grid container spacing={3}>
                  {tutorialData.map((tutorial) => (
                    <Grid item xs={12} sm={6} md={4} key={tutorial.id}>
                      <Card 
                        variant="outlined" 
                        sx={{ 
                          height: '100%', 
                          display: 'flex', 
                          flexDirection: 'column',
                          transition: 'transform 0.2s, box-shadow 0.2s',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: 3
                          }
                        }}
                      >
                        <Box 
                          sx={{ 
                            position: 'relative',
                            height: 180,
                            backgroundImage: `url(${tutorial.thumbnail})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center'
                          }}
                        >
                          {tutorial.type === 'video' && (
                            <IconButton 
                              sx={{ 
                                position: 'absolute', 
                                top: '50%', 
                                left: '50%', 
                                transform: 'translate(-50%, -50%)',
                                bgcolor: 'rgba(255, 255, 255, 0.8)',
                                '&:hover': {
                                  bgcolor: 'white'
                                }
                              }}
                            >
                              <PlayArrowIcon fontSize="large" color="primary" />
                            </IconButton>
                          )}
                          <Chip 
                            label={tutorial.type === 'video' ? `Video • ${tutorial.duration}` : 
                                  tutorial.type === 'article' ? `Artikel • ${tutorial.readTime} Lesezeit` : 
                                  `Webinar • ${tutorial.duration}`}
                            sx={{ 
                              position: 'absolute', 
                              bottom: 8, 
                              right: 8,
                              bgcolor: 'rgba(255, 255, 255, 0.9)'
                            }}
                            size="small"
                            icon={tutorial.type === 'video' ? <VideoLibraryIcon /> : 
                                 tutorial.type === 'article' ? <MenuBookIcon /> : 
                                 <SchoolIcon />}
                          />
                        </Box>
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                            {tutorial.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" paragraph>
                            {tutorial.description}
                          </Typography>
                        </CardContent>
                        <Box sx={{ p: 2, pt: 0, display: 'flex', justifyContent: 'space-between' }}>
                          <Button 
                            variant="text" 
                            color="primary" 
                            startIcon={tutorial.type === 'video' ? <PlayArrowIcon /> : <MenuBookIcon />}
                            href={tutorial.url}
                          >
                            {tutorial.type === 'video' ? 'Ansehen' : 
                             tutorial.type === 'article' ? 'Lesen' : 
                             'Teilnehmen'}
                          </Button>
                          <IconButton color="primary">
                            <BookmarkIcon />
                          </IconButton>
                        </Box>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
            
            {/* Support-Kontakt-Bereich */}
            {activeTab === 2 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Support-Optionen
                  </Typography>
                  <Typography variant="body1" paragraph color="text.secondary">
                    Wählen Sie aus verschiedenen Möglichkeiten, um mit unserem spezialisierten Support-Team für datenhaltende Institutionen in Kontakt zu treten.
                  </Typography>
                  
                  <Grid container spacing={2}>
                    {supportContacts.map((contact, index) => (
                      <Grid item xs={12} sm={6} key={index}>
                        <Card variant="outlined" sx={{ height: '100%' }}>
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <Avatar sx={{ bgcolor: 'primary.light', mr: 2 }}>
                                {contact.icon}
                              </Avatar>
                              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {contact.title}
                              </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary" paragraph>
                              {contact.description}
                            </Typography>
                            <Typography variant="body1" gutterBottom sx={{ fontWeight: 500 }}>
                              {contact.contact}
                            </Typography>
                            <Button 
                              variant="outlined" 
                              color="primary" 
                              fullWidth
                              href={contact.url}
                              sx={{ mt: 1 }}
                            >
                              {contact.action}
                            </Button>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                        Support-Ticket erstellen
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        Beschreiben Sie Ihr Anliegen, und unser Support-Team wird sich innerhalb von 24 Stunden bei Ihnen melden.
                      </Typography>
                      
                      <form onSubmit={handleTicketSubmit}>
                        <TextField
                          fullWidth
                          label="Betreff"
                          variant="outlined"
                          margin="normal"
                          required
                          value={ticketSubject}
                          onChange={(e) => setTicketSubject(e.target.value)}
                        />
                        <TextField
                          fullWidth
                          label="Beschreibung"
                          variant="outlined"
                          margin="normal"
                          required
                          multiline
                          rows={6}
                          value={ticketMessage}
                          onChange={(e) => setTicketMessage(e.target.value)}
                          placeholder="Beschreiben Sie Ihr Problem oder Ihre Frage so detailliert wie möglich..."
                        />
                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                          <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            startIcon={<SendIcon />}
                            disabled={!ticketSubject.trim() || !ticketMessage.trim()}
                          >
                            Ticket senden
                          </Button>
                        </Box>
                      </form>
                    </CardContent>
                  </Card>
                  
                  <Card variant="outlined" sx={{ mt: 3 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                        Geschäftszeiten
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Montag - Freitag:
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2">
                            9:00 - 17:00 Uhr
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Samstag - Sonntag:
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2">
                            Geschlossen
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Feiertage:
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2">
                            Geschlossen
                          </Typography>
                        </Grid>
                      </Grid>
                      <Alert severity="info" sx={{ mt: 2 }}>
                        Außerhalb der Geschäftszeiten können Sie ein Ticket erstellen oder eine E-Mail senden. Wir werden Ihnen am nächsten Arbeitstag antworten.
                      </Alert>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
            
            {/* Dokumentations-Bereich */}
            {activeTab === 3 && (
              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Dokumentation und Ressourcen
                </Typography>
                <Typography variant="body1" paragraph color="text.secondary">
                  Hier finden Sie umfassende Dokumentationen, Handbücher und technische Ressourcen zur ARGUS-Platform für Krankenhäuser und datenhaltende Institutionen.
                </Typography>
                
                <Grid container spacing={3}>
                  {documentationData.map((doc, index) => (
                    <Grid item xs={12} sm={6} md={3} key={index}>
                      <Card 
                        variant="outlined" 
                        sx={{ 
                          height: '100%',
                          transition: 'transform 0.2s, box-shadow 0.2s',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: 3
                          }
                        }}
                      >
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Avatar sx={{ bgcolor: 'primary.light', mr: 2 }}>
                              {doc.icon}
                            </Avatar>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {doc.title}
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary" paragraph>
                            {doc.description}
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                            <Chip 
                              label={`${doc.format} • ${doc.size}`} 
                              size="small" 
                              variant="outlined"
                            />
                            <Button
                              variant="text"
                              color="primary"
                              startIcon={<DownloadIcon />}
                              href={doc.url}
                            >
                              Download
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
                
                <Box sx={{ mt: 4 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Entwicklerressourcen
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                            Integrations-Leitfaden
                          </Typography>
                          <Typography variant="body2" color="text.secondary" paragraph>
                            Vollständige Dokumentation zur Integration der ARGUS-Platform mit bestehenden Krankenhausinformationssystemen und klinischen Datenbanken.
                          </Typography>
                          <Button
                            variant="outlined"
                            color="primary"
                            href="#"
                          >
                            Zur API-Dokumentation
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                            Institutionelles Netzwerk
                          </Typography>
                          <Typography variant="body2" color="text.secondary" paragraph>
                            Treten Sie unserem Netzwerk von Krankenhäusern und Forschungseinrichtungen bei, um Erfahrungen auszutauschen und Best Practices für die Verwaltung von Datenanfragen zu teilen.
                          </Typography>
                          <Button
                            variant="outlined"
                            color="primary"
                            href="#"
                          >
                            Community beitreten
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
                
                <Box sx={{ mt: 4, textAlign: 'center' }}>
                  <Typography variant="body1" paragraph>
                    Haben Sie nicht gefunden, wonach Sie suchen?
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setActiveTab(2)}
                  >
                    Kontaktieren Sie uns
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
      
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
          Ihr Support-Ticket wurde erfolgreich gesendet. Unser spezialisiertes Team für institutionellen Support wird sich innerhalb von 24 Stunden bei Ihnen melden.
        </Alert>
      </Snackbar>
    </MainLayout>
  );
};

export default HelpSupportPage;
